import {
   Component,
   EventEmitter,
   Input,
   OnChanges,
   OnDestroy,
   OnInit,
   Output,
   SimpleChanges,
   ViewChild
} from '@angular/core';
import NutrientViewer, { ToolbarItem } from '@nutrient-sdk/viewer';
import { firstValueFrom, Subscription } from 'rxjs';

import { Task } from '../../domain/task';
import { UploadFile } from '../../domain/uploadfile';
import { WorkflowFile } from '../../domain/workflow-file';
import { Annotation } from '../../domain/annotation';

import { TaskService } from '../../services/task.service';
import { UploadWorkflowFileComponent } from '../upload-workflow-file/upload-workflow-file.component';
import { environment } from '../../../environments/environment';
import * as jose from 'jose';
import DataUtils from '../../util/data-utils';
import { PermissionsStoreService } from '../../services/stores/permission-store-service';
import { UserPermissions } from '../../domain/security/user-permissions';
import { PhobosTaskInstance } from '../../domain/phobos-task-instance';
import { PhobosTaskInstanceService } from '../../services/phobos-task-instance.service';

@Component({
   selector: 'app-pdf-file-panel',
   templateUrl: './pdf-file-panel.component.html',
   styleUrls: ['./pdf-file-panel.component.scss']
})
export class PdfFilePanelComponent implements OnChanges, OnInit, OnDestroy {
   @ViewChild('fileUploadForm')
   uploadForm: UploadWorkflowFileComponent | undefined;

   @Input() hasPdfReport: boolean = false;
   @Input() isLoading: boolean = false;
   @Input() isPrimary: boolean = false;

   @Input() currentTask: Task = new Task();
   @Input() containerId: string = '';
   @Input() currentFile?: WorkflowFile;

   @Input() set hasFile(value: boolean) {
      this.hasPdfReport = value;
   }

   @Input() showPanelPadding: string = '';

   // Optional (phobos context uses this for read-only + supporting file download)
   @Input() phobosTaskInstance?: PhobosTaskInstance;

   @Output() uploadFile = new EventEmitter<{ event: any; file: UploadFile[] }>();

   uploadedFiles: UploadFile[] = [];

   private userPermissions: UserPermissions = new UserPermissions();
   private permissionsReady: Promise<UserPermissions>;
   private subscriptions = new Subscription();

   private lastLoadedKey?: string;

   constructor(
      private taskService: TaskService,
      private permissionsStore: PermissionsStoreService,
      private phobosTaskInstanceService: PhobosTaskInstanceService
   ) {
      this.permissionsReady = firstValueFrom(this.permissionsStore.get())
         .then((permissions) => {
            this.userPermissions = permissions ?? new UserPermissions();
            return this.userPermissions;
         })
         .catch(() => {
            this.userPermissions = new UserPermissions();
            return this.userPermissions;
         });
   }

   async ngOnInit(): Promise<void> {
      await this.permissionsReady;
   }

   ngOnDestroy(): void {
      this.subscriptions.unsubscribe();
      NutrientViewer.unload(`#${this.containerId}`);
   }

   emitUploadWorkflowFile(): void {
      this.uploadFile.emit({ event: null, file: this.uploadedFiles });
   }

   private resetUploadState(): void {
      this.uploadedFiles = [];
      this.uploadForm?.clearUploadForm();
   }

   private saveUserZoomPreference(zoomLevel: number): void {
      localStorage.setItem('userZoomPreference', zoomLevel.toString());
   }

   private getUserZoomPreference(): number {
      const storedZoom = localStorage.getItem('userZoomPreference');
      return storedZoom ? parseFloat(storedZoom) : -2;
   }

   ngOnChanges(changes: SimpleChanges): void {
      const nextFile = changes['currentFile']?.currentValue as
         | WorkflowFile
         | undefined;
      if (!nextFile) return;

      const nextKey = `${nextFile.documentId ?? ''}|${nextFile.storageId ?? ''}|${nextFile.id ?? ''}`;
      if (nextKey === this.lastLoadedKey) return;

      this.lastLoadedKey = nextKey;

      // Always reset upload state when file changes
      this.resetUploadState();

      // If no PDF document is available, unload viewer and stop
      if (!nextFile.documentId) {
         NutrientViewer.unload(`#${this.containerId}`);
         return;
      }

      void this.permissionsReady.then(() => this.renderPdf(nextFile));
   }

   private renderPdf(file: WorkflowFile): void {
      // Rebuild toolbar items
      const toolbarItems: string[] = [
         'zoom-out',
         'zoom-in',
         'zoom-mode',
         'export-pdf',
         'pager',
         'pan'
      ];

      if (file.isPrimary) {
         toolbarItems.push('rectangle', 'arrow', 'note', 'highlighter', 'ink-eraser');
      } else {
         toolbarItems.push('custom');
      }

      const downloadButton: ToolbarItem = {
         type: 'custom',
         id: 'download-source',
         title: 'Download',
         onPress: () => this.downloadSourceFile(file)
      };

      const customButtons: ToolbarItem[] = [downloadButton];

      NutrientViewer.unload(`#${this.containerId}`);

      if (file.storageId) {
         void this.loadFromServer(file, toolbarItems, customButtons);
      } else {
         this.loadFromBase64(file, toolbarItems, customButtons);
      }
   }

   private downloadSourceFile(file: WorkflowFile): void {
      if (file.data) {
         DataUtils.downloadBase64File(file);
         return;
      }

      // Phobos supporting files (server fetch)
      if (file.documentId && file.id) {
         this.phobosTaskInstanceService
            .getSupportingFileSourceByPhobosTaskInstance(file.documentId, file.id)
            .subscribe((f) => DataUtils.downloadBase64File(f));
      }
   }

   private loadFromBase64(
      file: WorkflowFile,
      toolbarItems: string[],
      customButtons: ToolbarItem[]
   ): void {
      const annotationsInstantJson: any[] = [];

      if (file.isPrimary && Array.isArray(file.annotations)) {
         for (const annotation of file.annotations) {
            annotationsInstantJson.push(JSON.parse(annotation.instantJson));
         }
      }

      NutrientViewer.load({
         serverUrl: environment.PSPDFKit.serverUrl,
         baseUrl: location.protocol + '//' + location.host + '/assets/',
         document: `data:${file.mimeType};base64,${file.data}`,
         container: `#${this.containerId}`,
         licenseKey: environment.PSPDFKit.key,
         instantJSON: {
            annotations: annotationsInstantJson,
            format: 'https://pspdfkit.com/instant-json/v1'
         },
         initialViewState: new NutrientViewer.ViewState({
            zoom: this.getUserZoomPreference()
         })
      }).then((instance) => {
         const items: ToolbarItem[] = instance.toolbarItems.concat(customButtons);

         const presets = NutrientViewer.defaultAnnotationPresets;
         presets.rectangle = {
            blendMode: 'normal',
            opacity: 100,
            color: NutrientViewer.Color.BLACK,
            strokeColor: NutrientViewer.Color.BLACK,
            lineWidth: 10
         };
         presets.arrow = {
            blendMode: 'normal',
            opacity: 100,
            color: NutrientViewer.Color.BLACK,
            strokeColor: NutrientViewer.Color.BLACK,
            lineWidth: 10
         };
         presets.highlighter = {
            blendMode: 'normal',
            color: NutrientViewer.Color.YELLOW,
            strokeColor: NutrientViewer.Color.YELLOW,
            opacity: 0.3,
            lineWidth: 10
         };

         instance.addEventListener('viewState.change', (newState) => {
            const zoomLevel = typeof newState.zoom === 'number' ? newState.zoom : -2;
            this.saveUserZoomPreference(zoomLevel);
         });

         instance.setAnnotationPresets(presets);
         instance.setToolbarItems(
            items.filter((item) => toolbarItems.includes(item.type))
         );

         if (this.userPermissions?.forms?.create) {
            instance.setToolbarItems((existing) => [
               ...existing,
               { type: 'form-creator' } as unknown as ToolbarItem
            ]);
         }

         // Persist annotations for base64 documents
         instance.addEventListener('annotations.update', (created) =>
            this.persistAnnotations(created)
         );
         instance.addEventListener('annotations.create', (created) =>
            this.persistAnnotations(created)
         );
      });
   }

   private async loadFromServer(
      file: WorkflowFile,
      toolbarItems: string[],
      customButtons: ToolbarItem[]
   ): Promise<void> {
      if (!file.storageId) return;

      const readOnly = !!this.phobosTaskInstance?.completedDate;
      const jwt = await this.createPdfJwt(file.storageId);

      NutrientViewer.load({
         serverUrl: environment.PSPDFKit.serverUrl,
         baseUrl: location.protocol + '//' + location.host + '/assets/',
         container: `#${this.containerId}`,
         licenseKey: environment.PSPDFKit.key,
         documentId: file.storageId,
         authPayload: { jwt },
         initialViewState: new NutrientViewer.ViewState({
            zoom: this.getUserZoomPreference(),
            readOnly
         }),
         instant: true,
         autoSaveMode: NutrientViewer.AutoSaveMode.IMMEDIATE
      })
         .then((instance) => {
            const items: ToolbarItem[] = instance.toolbarItems.concat(customButtons);

            const presets = NutrientViewer.defaultAnnotationPresets;
            presets.rectangle = {
               blendMode: 'normal',
               opacity: 100,
               color: NutrientViewer.Color.BLACK,
               strokeColor: NutrientViewer.Color.BLACK,
               lineWidth: 10
            };
            presets.arrow = {
               blendMode: 'normal',
               opacity: 100,
               color: NutrientViewer.Color.BLACK,
               strokeColor: NutrientViewer.Color.BLACK,
               lineWidth: 10
            };
            presets.highlighter = {
               blendMode: 'normal',
               color: NutrientViewer.Color.YELLOW,
               strokeColor: NutrientViewer.Color.YELLOW,
               opacity: 0.3,
               lineWidth: 10
            };

            instance.addEventListener('viewState.change', (newState) => {
               const zoomLevel =
                  typeof newState.zoom === 'number' ? newState.zoom : -2;
               this.saveUserZoomPreference(zoomLevel);
            });

            instance.setAnnotationPresets(presets);
            instance.setToolbarItems(
               items.filter((item) => toolbarItems.includes(item.type))
            );

            if (this.userPermissions?.forms?.create) {
               instance.setToolbarItems((existing) => [
                  ...existing,
                  { type: 'form-creator' } as unknown as ToolbarItem
               ]);
            }
         })
         .catch(() => console.error('Failed to load PDF from PSPDFKit server'));
   }

   private async createPdfJwt(documentId: string): Promise<string> {
      const jwtKey = environment.PSPDFKit.serverPem;

      const claims = {
         document_id: documentId,
         permissions: ['read-document', 'write', 'download']
      };

      const privateKey = await jose.importPKCS8(jwtKey, 'RS256');
      return await new jose.SignJWT(claims)
         .setProtectedHeader({ alg: 'RS256' })
         .setExpirationTime('3d')
         .sign(privateKey);
   }

   private persistAnnotations(createdAnnotations: any): void {
      if (!this.currentFile) return;
      for (const pdfAnnotation of createdAnnotations) {
         const annotation = new Annotation();

         const documentId = this.currentFile?.documentId;
         if (documentId) {
            annotation.documentId = documentId;
         }

         annotation.instantJson = JSON.stringify(
            NutrientViewer.Annotations.toSerializableObject(pdfAnnotation)
         );

         annotation.annotationId = pdfAnnotation.id;

         this.taskService
            .addAnnotation(annotation, this.currentTask.processId)
            .subscribe(() => {});
      }
   }
}
